import { Children, Fragment, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import {
    Button,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    useDisclosure,
} from '@nextui-org/react';
import { CtyButton } from '../button/CtyButton';
import { CardUI } from '../card/cardui';
import { SimpleDeleteConfirmationModal } from './simpleDeleteConfirmationModal';
import CtyPromptInput from '../promptinput/Cty-PromptInput';
import { promptInputType } from '../utils/constants';
import { AgGridTable } from './aggridtable';
import { ErrorBoundary } from './ErrorBoundary';
import { convertToLocalDayMonthYear } from '../utils/dateutils';

/* ------------------------------------------------------------------ */
/*  CardListViewMode + ViewModeToggle                                 */
/* ------------------------------------------------------------------ */

export enum CardListViewMode {
    GRID = 'grid',
    LIST = 'list',
}

interface ViewModeToggleProps {
    readonly viewMode: CardListViewMode;
    readonly onViewModeChange: (mode: CardListViewMode) => void;
}

export function ViewModeToggle({ viewMode, onViewModeChange }: ViewModeToggleProps) {
    const { t } = useTranslation();

    return (
        <div className="flex items-start gap-2">
            <CtyButton
                isIconOnly={true}
                variant={viewMode === CardListViewMode.GRID ? 'solid' : 'bordered'}
                color={viewMode === CardListViewMode.GRID ? 'primary' : 'default'}
                className="border-1"
                size="md"
                onPress={() => onViewModeChange(CardListViewMode.GRID)}
                title={t('Grid')}
            >
                <Icon icon="material-symbols:grid-on" width="18" />
            </CtyButton>
            <CtyButton
                isIconOnly={true}
                variant={viewMode === CardListViewMode.LIST ? 'solid' : 'bordered'}
                color={viewMode === CardListViewMode.LIST ? 'primary' : 'default'}
                className="border-1"
                size="md"
                onPress={() => onViewModeChange(CardListViewMode.LIST)}
                title={t('List')}
            >
                <Icon icon="material-symbols:list" width="18" />
            </CtyButton>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  EmptyState                                                        */
/* ------------------------------------------------------------------ */

interface EmptyStateProps {
    readonly message: string;
}

export function EmptyState({ message }: EmptyStateProps) {
    return (
        <div className="flex-1 flex items-center justify-center h-full border-2 border-default-300 rounded-lg m-4 bg-default-100">
            <div className="text-2xl text-primary">{message}</div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  CardGrid – responsive 3-column grid used by every card list page  */
/* ------------------------------------------------------------------ */

interface CardGridProps {
    readonly children: ReactNode;
    readonly className?: string;
}

export function CardGrid({ children, className }: CardGridProps) {
    return (
        <div
            className={
                className ??
                'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:4 md:3 lg:3 shadow-none'
            }
        >
            {children}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  SearchWithViewToggle – search bar + optional grid/list toggle     */
/* ------------------------------------------------------------------ */

interface SearchWithViewToggleProps {
    readonly searchId: string;
    readonly searchPlaceholder: string;
    readonly onSearch: (query: string) => void;
    readonly viewMode?: CardListViewMode;
    readonly onViewModeChange?: (mode: CardListViewMode) => void;
    readonly className?: string;
}

export function SearchWithViewToggle({
    searchId,
    searchPlaceholder,
    onSearch,
    viewMode,
    onViewModeChange,
    className = 'flex gap-2 pb-0',
}: SearchWithViewToggleProps) {
    return (
        <div className={className}>
            <div className="flex-1">
                <CtyPromptInput
                    id={searchId}
                    elementType={promptInputType.INPUT}
                    enableSearch={true}
                    enableSendBtn={true}
                    handleSendClick={onSearch}
                    placeholder={searchPlaceholder}
                    canSendEmptyMessage={true}
                    clearInputOnSend={false}
                    triggerEveryInputChange={true}
                />
            </div>
            {viewMode !== undefined && onViewModeChange && (
                <ViewModeToggle viewMode={viewMode} onViewModeChange={onViewModeChange} />
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  EntityCardWithActions – CardUI + dropdown (delete) + modal        */
/* ------------------------------------------------------------------ */

interface EntityCardWithActionsProps {
    readonly id: string;
    readonly name: string;
    readonly description: string;
    readonly bodyExtra?: JSX.Element;
    readonly view?: boolean;
    readonly footerItems: JSX.Element;
    readonly canDelete: boolean;
    readonly dropdownAriaLabel: string;
    readonly deleteModalEntityName: string;
    readonly onConfirmDelete: (closeModal: () => void) => void;
    readonly isDeleteLoading: boolean;
    readonly deleteConfirmLabel: string;
    /** The menu item that opens the dialog. Default: "Delete". */
    readonly deleteMenuLabel?: string;
    /** The dialog's title and message, for an action that is not a deletion. Default: "Confirm deletion" and "… permanently removed …". */
    readonly deleteModalTitle?: ReactNode;
    readonly deleteModalMessage?: ReactNode;
    /** false drops the "type delete to confirm" step, for an action that can be undone. Default: true. */
    readonly deleteRequiresTypedConfirmation?: boolean;
}

export function EntityCardWithActions({
    id,
    name,
    description,
    bodyExtra,
    view,
    footerItems,
    canDelete,
    dropdownAriaLabel,
    deleteModalEntityName,
    onConfirmDelete,
    isDeleteLoading,
    deleteConfirmLabel,
    deleteMenuLabel,
    deleteModalTitle,
    deleteModalMessage,
    deleteRequiresTypedConfirmation,
}: EntityCardWithActionsProps) {
    const { t } = useTranslation();
    const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();

    const dropDownElement = (
        <Dropdown>
            <DropdownTrigger>
                <Button
                    variant="light"
                    className="top-2 p-0 w-max absolute right-2 min-w-0"
                    startContent={
                        <Icon icon="entypo:dots-three-vertical" className="text-[#D9D9D9] dark:text-[#8a8a8a]" />
                    }
                />
            </DropdownTrigger>
            <DropdownMenu
                aria-label={dropdownAriaLabel}
                disabledKeys={canDelete ? [] : ['delete']}
            >
                <DropdownItem
                    key="delete"
                    className="text-danger"
                    title={deleteMenuLabel ?? t('Delete')}
                    onPress={onOpen}
                >
                    {deleteMenuLabel ?? t('Delete')}
                </DropdownItem>
            </DropdownMenu>
        </Dropdown>
    );

    return (
        <>
            <CardUI
                name={name}
                description={description}
                bodyExtra={bodyExtra}
                id={id}
                view={view}
                footerItems={footerItems}
                dropDownElement={dropDownElement}
            />
            <SimpleDeleteConfirmationModal
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                entityName={deleteModalEntityName}
                title={deleteModalTitle}
                body={deleteModalMessage}
                requireTypedConfirmation={deleteRequiresTypedConfirmation}
                onConfirm={() => onConfirmDelete(onClose)}
                isConfirmLoading={isDeleteLoading}
                cancelLabel={t('Close')}
                confirmLabel={deleteConfirmLabel}
            />
        </>
    );
}

/* ------------------------------------------------------------------ */
/*  ListActionButtonsCell – action buttons for ag-grid rows           */
/* ------------------------------------------------------------------ */

export interface ActionButtonConfig {
    readonly label: string;
    readonly onPress: () => void;
    readonly variant?: 'bordered' | 'solid';
    readonly color?: 'default' | 'primary' | 'danger';
    readonly isDisabled?: boolean;
}

interface ListActionButtonsCellProps {
    readonly actions: ActionButtonConfig[];
}

export function ListActionButtonsCell({ actions }: ListActionButtonsCellProps) {
    return (
        <div className="flex items-center gap-2 py-1 flex-nowrap">
            {actions.map((action) => (
                <CtyButton
                    key={action.label}
                    onPress={action.onPress}
                    className="border-1 h-7 min-w-[72px] px-2 text-xs whitespace-nowrap"
                    variant={action.variant ?? 'bordered'}
                    radius="sm"
                    size="sm"
                    color={action.color ?? 'default'}
                    isDisabled={action.isDisabled}
                >
                    {action.label}
                </CtyButton>
            ))}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  getActionsColumnDef – standard actions column for AgGrid tables   */
/* ------------------------------------------------------------------ */

export function getActionsColumnDef(config: {
    headerName: string;
    cellRenderer: (params: any) => JSX.Element;
    minWidth?: number;
    maxWidth?: number;
}) {
    return {
        headerName: config.headerName,
        field: 'actions' as const,
        sortable: false,
        filter: false,
        minWidth: config.minWidth ?? 200,
        ...(config.maxWidth ? { maxWidth: config.maxWidth } : {}),
        pinned: 'right' as const,
        lockPinned: true,
        lockPosition: true,
        cellRenderer: config.cellRenderer,
    };
}

/* ------------------------------------------------------------------ */
/*  ListPageContent – content wrapper for grid / list views           */
/* ------------------------------------------------------------------ */

interface ListPageContentProps {
    readonly viewMode: CardListViewMode;
    readonly children: ReactNode;
    readonly className?: string;
}

export function ListPageContent({ viewMode, children, className }: ListPageContentProps) {
    return (
        <div
            className={
                className ??
                `flex-1 p-1 ${viewMode === CardListViewMode.LIST ? 'overflow-hidden' : 'overflow-y-auto'}`
            }
        >
            {children}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  ListActionsCellWithDelete – ListActionButtonsCell + delete modal  */
/* ------------------------------------------------------------------ */

interface ListActionsCellWithDeleteProps {
    readonly actions: ActionButtonConfig[];
    readonly canDelete: boolean;
    readonly entityName: string;
    readonly onConfirmDelete: (closeModal: () => void) => void;
    readonly isDeleteLoading: boolean;
    readonly deleteConfirmLabel: string;
    readonly deleteButtonLabel?: string;
    /** The dialog's title and message, for an action that is not a deletion. Default: "Confirm deletion" and "… permanently removed …". */
    readonly deleteModalTitle?: ReactNode;
    readonly deleteModalMessage?: ReactNode;
    /** false drops the "type delete to confirm" step, for an action that can be undone. Default: true. */
    readonly deleteRequiresTypedConfirmation?: boolean;
}

export function ListActionsCellWithDelete({
    actions,
    canDelete,
    entityName,
    onConfirmDelete,
    isDeleteLoading,
    deleteConfirmLabel,
    deleteButtonLabel,
    deleteModalTitle,
    deleteModalMessage,
    deleteRequiresTypedConfirmation,
}: ListActionsCellWithDeleteProps) {
    const { t } = useTranslation();
    const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();

    return (
        <>
            <ListActionButtonsCell
                actions={[
                    ...actions,
                    {
                        label: deleteButtonLabel ?? t('Delete'),
                        onPress: onOpen,
                        variant: 'bordered',
                        color: 'danger',
                        isDisabled: !canDelete,
                    },
                ]}
            />
            <SimpleDeleteConfirmationModal
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                entityName={entityName}
                title={deleteModalTitle}
                body={deleteModalMessage}
                requireTypedConfirmation={deleteRequiresTypedConfirmation}
                onConfirm={() => onConfirmDelete(onClose)}
                isConfirmLoading={isDeleteLoading}
                cancelLabel={t('Close')}
                confirmLabel={deleteConfirmLabel}
            />
        </>
    );
}

/* ------------------------------------------------------------------ */
/*  Common AgGrid column helpers                                      */
/* ------------------------------------------------------------------ */

export function getNameColumnDef(headerName: string, field = 'name', minWidth = 220) {
    return {
        headerName,
        field,
        sortable: true,
        minWidth,
        flex: 1,
        filter: false,
    };
}

export function getDescriptionColumnDef(
    headerName: string,
    field = 'description',
    minWidth = 260
) {
    return {
        headerName,
        field,
        sortable: true,
        minWidth,
        flex: 1.5,
        filter: false,
        valueFormatter: (params: any) => params?.value || '-',
    };
}

export function getDateColumnDef(
    headerName: string,
    field = 'updated_at',
    minWidth = 200
) {
    return {
        headerName,
        field,
        sortable: true,
        minWidth,
        filter: false,
        valueFormatter: (params: any) => {
            if (!params?.value) return '-';
            return convertToLocalDayMonthYear(params.value);
        },
    };
}

/* ------------------------------------------------------------------ */
/*  EntityListView – unified search + grid/list view container        */
/* ------------------------------------------------------------------ */

interface EntityListViewProps<T> {
    readonly items: T[] | undefined | null;
    readonly isLoading?: boolean;
    readonly error?: unknown;

    readonly searchId: string;
    readonly searchPlaceholder: string;
    readonly onSearch: (query: string) => void;

    readonly viewMode: CardListViewMode;
    readonly onViewModeChange: (mode: CardListViewMode) => void;

    readonly emptyMessage: string;

    readonly renderCard: (item: T) => ReactNode;
    readonly cardKey: (item: T) => string;
    readonly extraGridChildren?: ReactNode;
    readonly cardGridClassName?: string;

    readonly tableId: string;
    readonly columnDefs: any[];
    readonly listRowMapper?: (item: T) => any;
}

function GridContent<T>({
    items,
    renderCard,
    cardKey,
    extraGridChildren,
    cardGridClassName,
}: {
    readonly items: T[];
    readonly renderCard: (item: T) => ReactNode;
    readonly cardKey: (item: T) => string;
    readonly extraGridChildren?: ReactNode;
    readonly cardGridClassName?: string;
}) {
    return (
        <CardGrid className={cardGridClassName}>
            {extraGridChildren}
            {items.map((item) => (
                <Fragment key={cardKey(item)}>{renderCard(item)}</Fragment>
            ))}
        </CardGrid>
    );
}

const GridContentWithBoundary = ErrorBoundary(GridContent as any);

export function EntityListView<T>({
    items,
    isLoading,
    error,
    searchId,
    searchPlaceholder,
    onSearch,
    viewMode,
    onViewModeChange,
    emptyMessage,
    renderCard,
    cardKey,
    extraGridChildren,
    cardGridClassName,
    tableId,
    columnDefs,
    listRowMapper,
}: EntityListViewProps<T>) {
    const list = useMemo(() => items ?? [], [items]);
    const listRows = useMemo(
        () => (listRowMapper ? list.map(listRowMapper) : (list as unknown as any[])),
        [list, listRowMapper]
    );

    // Extra cards (e.g. built-in ones) count: the grid is only empty with neither.
    const isEmpty = !isLoading && !error && list.length === 0 && Children.count(extraGridChildren) === 0;

    return (
        <div className="flex flex-col gap-3 flex-1 h-full min-h-0">
            <SearchWithViewToggle
                searchId={searchId}
                searchPlaceholder={searchPlaceholder}
                onSearch={onSearch}
                viewMode={viewMode}
                onViewModeChange={onViewModeChange}
            />
            {isEmpty ? (
                <EmptyState message={emptyMessage} />
            ) : (
                <ListPageContent viewMode={viewMode}>
                    {viewMode === CardListViewMode.LIST ? (
                        <AgGridTable
                            tableId={tableId}
                            tableData={listRows}
                            tableColumnDefs={columnDefs}
                            isLoading={isLoading}
                        />
                    ) : (
                        <GridContentWithBoundary
                            isLoading={isLoading}
                            error={error}
                            items={list}
                            renderCard={renderCard}
                            cardKey={cardKey}
                            extraGridChildren={extraGridChildren}
                            cardGridClassName={cardGridClassName}
                        />
                    )}
                </ListPageContent>
            )}
        </div>
    );
}
